/**
 * Output Format Section Component
 * Displays output format settings with schema selection from centralized table
 */

import React from 'react';
import { FileJson, ArrowRight, Type, Code, FileCode, Info, Star } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ButtonGroup } from '@/components/ui/ButtonGroup';
import { getConfigValue } from '@/config/fieldConfig';
import { useI18n } from '@/i18n';
import { useOutputSchemas } from '@/features/system-settings/hooks/useOutputSchemas';
import type { OutputSchema } from '@/types/outputSchema';

interface OutputFormatSectionProps {
  values: Record<string, any>;
  onChange: (name: string, value: any) => void;
  onNavigateToSchemas: () => void;
  appContext?: any;
}

export const OutputFormatSection: React.FC<OutputFormatSectionProps> = ({
  values,
  onChange,
  onNavigateToSchemas,
}) => {
  const { messages, language } = useI18n();
  const t = messages.agentConfig;

  const outputFormat = getConfigValue(values, 'output_format') || 'default';

  // NEW: Use reference-based schema keys
  const enabledSchemaKeys: string[] = values.enabled_schema_keys || [];
  const defaultSchemaKey: string | null = values.default_schema_key || null;

  // Fetch enabled schemas from centralized table
  const { data } = useOutputSchemas({ enabled: true });
  const allSchemas: OutputSchema[] = data?.schemas || [];
  const enabledSchemas = allSchemas.filter((s: OutputSchema) => enabledSchemaKeys.includes(s.key));
  const defaultSchema = enabledSchemas.find((s: OutputSchema) => s.key === defaultSchemaKey);

  // Check if JSON is selected but no schemas enabled
  const showNoSchemasWarning = outputFormat === 'json' && enabledSchemaKeys.length === 0;

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      {/* Section Header */}
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileJson className="w-5 h-5 text-gray-500" />
          <h3 className="text-lg font-semibold text-gray-800">
            {t.outputFormat?.title || 'Output Format'}
          </h3>
        </div>
        {outputFormat === 'json' && (
          <Button
            variant="secondary"
            size="sm"
            onClick={onNavigateToSchemas}
            className="flex items-center gap-1"
          >
            {t.outputFormat?.manageSchemas || 'Manage Schemas'}
            <ArrowRight className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Section Content */}
      <div className="p-4 space-y-4">
        {/* Output Format Button Group */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {messages.fields?.output_format?.label || 'Output Format'}
          </label>
          <ButtonGroup
            options={[
              {
                value: 'default',
                label: messages.fields?.output_format?.options?.default || 'Default',
                icon: <Type className="w-4 h-4" />,
              },
              {
                value: 'markdown',
                label: messages.fields?.output_format?.options?.markdown || 'Markdown',
                icon: <Code className="w-4 h-4" />,
              },
              {
                value: 'json',
                label: messages.fields?.output_format?.options?.json || 'JSON',
                icon: <FileCode className="w-4 h-4" />,
              },
            ]}
            value={outputFormat}
            onChange={(value) => onChange('output_format', value)}
          />
          <p className="text-xs text-gray-500 mt-1">
            {messages.fields?.output_format?.help || 'Response output format'}
          </p>
        </div>

        {/* Warning: No schemas enabled */}
        {showNoSchemasWarning && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-amber-800">
              {t.outputFormat?.noSchemasWarning ||
                'No output schemas enabled. Enable schemas in the Output Schemas tab.'}
            </p>
            <Button
              variant="secondary"
              size="sm"
              onClick={onNavigateToSchemas}
              className="mt-2"
            >
              {t.outputFormat?.manageSchemas || 'Enable Schemas'}
            </Button>
          </div>
        )}

        {/* Default Schema Display (only shown for JSON with enabled schemas) */}
        {outputFormat === 'json' && enabledSchemaKeys.length > 0 && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Star className={`w-4 h-4 ${defaultSchema ? 'fill-amber-500 text-amber-500' : 'text-gray-400'}`} />
                <span className="text-sm font-medium text-gray-700">
                  {language === 'zh' ? '默认 Schema:' : 'Default Schema:'}
                </span>
                {defaultSchema ? (
                  <span className="inline-flex items-center gap-1.5 bg-white border border-gray-200 rounded px-2 py-0.5">
                    <code className="text-sm font-mono text-blue-700">{defaultSchema.key}</code>
                    <span className="text-xs text-gray-500">{defaultSchema.name}</span>
                  </span>
                ) : (
                  <span className="text-sm text-gray-500 italic">
                    {t.outputFormat?.noDefault || 'None set'}
                  </span>
                )}
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={onNavigateToSchemas}
              >
                {t.outputFormat?.manageSchemas || 'Manage Schemas'}
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {t.outputFormat?.defaultSchemaHelp ||
                'Set a default schema in the Output Schemas tab.'}
            </p>
          </div>
        )}

        {/* Available Schemas List (API Reference) */}
        {outputFormat === 'json' && enabledSchemaKeys.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-800 mb-2">
                  {t.outputFormat?.availableSchemas || 'Enabled Schema Keys (for API)'}
                </p>
                <div className="flex flex-wrap gap-2">
                  {enabledSchemas.map((schema) => (
                    <div
                      key={schema.key}
                      className={`inline-flex items-center gap-1.5 bg-white border rounded px-2 py-1 ${
                        schema.key === defaultSchemaKey ? 'border-amber-300 bg-amber-50' : 'border-blue-200'
                      }`}
                    >
                      {schema.key === defaultSchemaKey && (
                        <Star className="w-3 h-3 fill-amber-500 text-amber-500" />
                      )}
                      <code className="text-xs font-mono text-blue-700">{schema.key}</code>
                      <span className="text-xs text-gray-500">{schema.name}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-blue-600 mt-2">
                  {t.outputFormat?.apiHint ||
                    'Pass output_schema_key in API request to use a specific schema.'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OutputFormatSection;
